import axios from 'axios';
import { getBackendBaseUrl } from '@/lib/backendUrl';
import { shouldHandleUnauthorized } from '@/lib/authFailure';

const BACKEND_URL = getBackendBaseUrl();

export const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for adding auth token
api.interceptors.request.use(
  (config) => {
    // Token is handled via cookies, but we can also use localStorage as fallback
    const token = localStorage.getItem('leaguehub-token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Public routes where a 401 must NOT bounce the user back to the login page.
// Kept in sync with the public <Route> entries in App.js.
const PUBLIC_NO_REDIRECT_PATHS = new Set([
  '/',
  '/forgot-password',
  '/install-pwa',
  '/business',
  '/privacy',
  '/account-deletion',
  '/help',
]);

const isPublicNoRedirectPath = (pathname) => {
  if (PUBLIC_NO_REDIRECT_PATHS.has(pathname)) return true;
  if (pathname.startsWith('/p/')) return true; // public facility pages
  return false;
};

// Response interceptor for handling errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const currentToken = localStorage.getItem('leaguehub-token');
      // A request started before OAuth completed must not erase the fresh token.
      if (!shouldHandleUnauthorized(error, currentToken)) {
        return Promise.reject(error);
      }
      localStorage.removeItem('leaguehub-token');
      // Redirect to login only when on a protected page. Background polling
      // (notifications, /auth/me) on public pages would otherwise yank the
      // user away from /forgot-password, public facility links, etc.
      const hash = window.location.hash || '';
      const isOAuthCallback = hash.includes('session_id=') || hash.includes('auth_token=');
      const onPublicPage = isPublicNoRedirectPath(window.location.pathname);
      if (!onPublicPage && !isOAuthCallback) {
        window.location.href = '/';
      }
    }
    return Promise.reject(error);
  }
);

// API helper functions
export const authAPI = {
  exchangeSession: (sessionId) => api.post('/auth/session', { session_id: sessionId }),
  completeMobileAuth: (data) => api.post('/auth/mobile/complete', data),
  startAppleAuth: (data) => api.post('/auth/apple/start', data),
  completeAppleAuth: (data) => api.post('/auth/apple/native', data),
  getMe: () => api.get('/auth/me', { timeout: 8000 }),
  logout: () => api.post('/auth/logout'),
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  requestPhoneOtp: (data) => api.post('/auth/phone/request', data),
  verifyPhoneOtp: (data) => api.post('/auth/phone/verify', data),
  requestPasswordReset: (email) => api.post('/auth/password/reset-request', { email }),
  verifyPasswordReset: (data) => api.post('/auth/password/reset-verify', data),
};

export const sportsAPI = {
  getAll: () => api.get('/sports'),
  getById: (id) => api.get(`/sports/${id}`),
};

export const leaguesAPI = {
  getAll: (params) => api.get('/leagues', { params }),
  getById: (id, params) => api.get(`/leagues/${id}`, { params }),
  create: (data) => api.post('/leagues', data),
  update: (id, data) => api.put(`/leagues/${id}`, data),
  join: (id, data) => api.post(`/leagues/${id}/join`, data),
  leave: (id) => api.post(`/leagues/${id}/leave`),
  updateMember: (leagueId, userId, data) => api.put(`/leagues/${leagueId}/members/${userId}`, data),
  changeMemberRole: (leagueId, userId, data) => api.put(`/leagues/${leagueId}/members/${userId}/role`, data),
  kickMember: (leagueId, userId) => api.delete(`/leagues/${leagueId}/members/${userId}`),
  addMember: (leagueId, userId) => api.post(`/leagues/${leagueId}/members`, { user_id: userId }),
  getMyLeagues: () => api.get('/my-leagues'),
  importData: (leagueId, file, mode = 'all') => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/leagues/${leagueId}/import`, formData, {
      params: { mode },
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  previewImport: (leagueId, file, mode = 'all') => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/leagues/${leagueId}/import/preview`, formData, {
      params: { mode },
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  confirmImport: (leagueId, payload) =>
    api.post(`/leagues/${leagueId}/import/confirm`, payload),
  downloadImportTemplate: (leagueId, templateType = 'all') =>
    api.get(`/leagues/${leagueId}/import/template`, {
      params: { template_type: templateType },
      responseType: 'blob',
    }),
  // Scoring System
  getScoring: (leagueId) => api.get(`/leagues/${leagueId}/scoring`),
  updateScoring: (leagueId, data) => api.put(`/leagues/${leagueId}/scoring`, data),
  // Point Adjustments
  getPointAdjustments: (leagueId) => api.get(`/leagues/${leagueId}/point-adjustments`),
  addPointAdjustment: (leagueId, data) => api.post(`/leagues/${leagueId}/point-adjustments`, data),
  setUserAdjustment: (leagueId, userId, data) => api.put(`/leagues/${leagueId}/point-adjustments/user/${userId}`, data),
  deletePointAdjustment: (leagueId, adjustmentId) => api.delete(`/leagues/${leagueId}/point-adjustments/${adjustmentId}`),
  deleteUserAdjustments: (leagueId, userId) => api.delete(`/leagues/${leagueId}/point-adjustments/user/${userId}`),
  // Season Reset
  getSeasons: (leagueId) => api.get(`/leagues/${leagueId}/seasons`),
  resetSeason: (leagueId, data) => api.post(`/leagues/${leagueId}/reset-season`, data),
  // Member Stats
  updateMemberStats: (leagueId, userId, data) => api.put(`/leagues/${leagueId}/members/${userId}/stats`, data),
  // Member attributes for the league's sport (team-builder inputs).
  // Pass only the fields you want to update; form_manual: null drops the override.
  updateMemberProfile: (leagueId, userId, { traits, skill_level, form_manual, preferred_foot, strong_foot, weak_foot, play_style } = {}) => {
    const body = {};
    if (traits !== undefined) body.traits = traits;
    if (skill_level !== undefined) body.skill_level = skill_level;
    if (form_manual !== undefined) body.form_manual = form_manual;
    if (preferred_foot !== undefined) body.preferred_foot = preferred_foot;
    if (strong_foot !== undefined) body.strong_foot = strong_foot;
    if (weak_foot !== undefined) body.weak_foot = weak_foot;
    if (play_style !== undefined) body.play_style = play_style;
    return api.patch(`/leagues/${leagueId}/members/${userId}/profile`, body);
  },
};

export const traitsAPI = {
  // Trait catalog plus the attribute schema of a sport (defaults to calcetto).
  getAll: (sportId) => api.get('/traits', { params: sportId ? { sport_id: sportId } : {} }),
};

export const matchesAPI = {
  getAll: (params) => api.get('/matches', { params }),
  getById: (id) => api.get(`/matches/${id}`),
  create: (data) => api.post('/matches', data),
  update: (id, data) => api.put(`/matches/${id}`, data),
  join: (id, data) => api.post(`/matches/${id}/join`, data),
  leave: (id) => api.post(`/matches/${id}/leave`),
  updateParticipant: (matchId, userId, data) => api.put(`/matches/${matchId}/participants/${userId}`, data),
  addParticipant: (matchId, data) => api.post(`/matches/${matchId}/participants`, data),
  removeParticipant: (matchId, userId) => api.delete(`/matches/${matchId}/participants/${userId}`),
  getRosterCandidates: (matchId) => api.get(`/matches/${matchId}/roster-candidates`),
  getMyMatches: () => api.get('/my-matches'),
  suggestTeams: (matchId, teamSize) =>
    api.post(`/matches/${matchId}/suggest-teams`, teamSize ? { team_size: teamSize } : {}),
  notifyTeams: (matchId) => api.post(`/matches/${matchId}/notify-teams`),
  stopSeries: (seriesId) => api.delete(`/match-series/${seriesId}`),
  getLeagueSeries: (leagueId) => api.get(`/leagues/${leagueId}/match-series`),
  updateSeries: (seriesId, data) => api.put(`/match-series/${seriesId}`, data),
};

export const statsAPI = {
  getMe: () => api.get('/stats/me'),
  getPlayer: (userId) => api.get(`/stats/player/${userId}`),
  getPlayerForm: (userId, limit = 5) => api.get(`/stats/player/${userId}/form`, { params: { limit } }),
  getLeagueLeaderboard: (leagueId) => api.get(`/stats/league/${leagueId}/leaderboard`),
  getLeagueTopScorers: (leagueId, limit = 50) => api.get(`/stats/league/${leagueId}/top-scorers`, { params: { limit } }),
  getCompetitionTopScorers: (competitionId, limit = 50) => api.get(`/stats/competition/${competitionId}/top-scorers`, { params: { limit } }),
  getLeagueMvp: (leagueId) => api.get(`/stats/league/${leagueId}/mvp`),
};

export const matchPaymentsAPI = {
  list: (matchId) => api.get(`/matches/${matchId}/payments`),
  create: (matchId, data) => api.post(`/matches/${matchId}/payments`, data),
  delete: (matchId, paymentId) => api.delete(`/matches/${matchId}/payments/${paymentId}`),
  confirm: (matchId, paymentId) => api.post(`/matches/${matchId}/payments/${paymentId}/confirm`),
  unconfirm: (matchId, paymentId) => api.post(`/matches/${matchId}/payments/${paymentId}/unconfirm`),
  checkout: (matchId, data) => api.post(`/matches/${matchId}/payments/checkout`, data),
  confirmQuota: (sessionId) => api.post('/match-quota/confirm', { session_id: sessionId }),
  openCollection: (matchId, data) => api.post(`/matches/${matchId}/collection`, data),
  collectorCandidates: (matchId) => api.get(`/matches/${matchId}/collection/candidates`),
  askCollector: (matchId, userId) => api.post(`/matches/${matchId}/collection/ask`, { user_id: userId }),
  closeCollection: (matchId) => api.delete(`/matches/${matchId}/collection`),
};

// Stripe Connect: who collects the field money (organizer or facility) receives it here.
export const connectAPI = {
  link: (data) => api.post('/stripe/connect/link', data),
  status: (facilityId) => api.get('/stripe/connect/status', { params: facilityId ? { facility_id: facilityId } : {} }),
};

export const competitionsAPI = {
  getAll: (params) => api.get('/competitions', { params }),
  getById: (id) => api.get(`/competitions/${id}`),
  create: (data) => api.post('/competitions', data),
  update: (id, data) => api.put(`/competitions/${id}`, data),
  getTeams: (id) => api.get(`/competitions/${id}/teams`),
  addTeam: (id, data) => api.post(`/competitions/${id}/teams`, data),
  updateTeam: (id, teamId, data) => api.put(`/competitions/${id}/teams/${teamId}`, data),
};

export const announcementsAPI = {
  getAll: (params) => api.get('/announcements', { params }),
  create: (data) => api.post('/announcements', data),
  delete: (id) => api.delete(`/announcements/${id}`),
};

export const notificationsAPI = {
  getAll: () => api.get('/notifications'),
  markAsRead: (id) => api.put(`/notifications/${id}/read`),
  markAllAsRead: () => api.put('/notifications/read-all'),
};

export const usersAPI = {
  deleteAccount: (confirmation) => api.delete('/me', { data: { confirmation } }),
};

export const profileAPI = {
  updatePlayer: (data) => api.put('/profile/player', data),
  updateReferee: (data) => api.put('/profile/referee', data),
  updateRole: (userId, role) => api.put(`/users/${userId}/role`, { role }),
  updateNickname: (nickname) => api.put('/profile/nickname', { nickname }),
  updateName: (name) => api.put('/profile/name', { name }),
  updatePicture: (picture) => api.put('/profile/picture', { picture }),
  getPublicProfile: (userId) => api.get(`/users/${userId}/public-profile`),
};

export const placesAPI = {
  search: (q, city) => api.get('/places/search', { params: { q, city } }),
  nearby: (lat, lng, radius_km, sport) => api.get('/places/nearby', { params: { lat, lng, radius_km, sport } }),
};

export const adminAPI = {
  getUsers: () => api.get('/admin/users'),
  getStats: () => api.get('/admin/stats'),
  getCollections: () => api.get('/admin/collections'),
};

export const facilitiesAPI = {
  getAll: (params) => api.get('/facilities', { params }),
  getById: (id) => api.get(`/facilities/${id}`),
  create: (data) => api.post('/facilities', data),
  update: (id, data) => api.put(`/facilities/${id}`, data),
  getMy: () => api.get('/my-facilities'),
  addManager: (id, data) => api.post(`/facilities/${id}/managers`, data),
  removeManager: (id, userId) => api.delete(`/facilities/${id}/managers/${userId}`),
  // Fields
  getFields: (id) => api.get(`/facilities/${id}/fields`),
  createField: (id, data) => api.post(`/facilities/${id}/fields`, data),
  updateField: (id, fieldId, data) => api.put(`/facilities/${id}/fields/${fieldId}`, data),
  deleteField: (id, fieldId) => api.delete(`/facilities/${id}/fields/${fieldId}`),
  // Availability
  getFieldAvailability: (facilityId, fieldId, date) =>
    api.get(`/facilities/${facilityId}/fields/${fieldId}/availability`, { params: { date } }),
  getFacilityAvailability: (facilityId, date, sportId) =>
    api.get(`/facilities/${facilityId}/availability`, { params: { date, sport_id: sportId } }),
  // Bookings
  getBookings: (id, params) => api.get(`/facilities/${id}/bookings`, { params }),
  createBooking: (id, data) => api.post(`/facilities/${id}/bookings`, data),
  updateBooking: (bookingId, data) => api.put(`/bookings/${bookingId}`, data),
  cancelBooking: (bookingId) => api.delete(`/bookings/${bookingId}`),
  getMyBookings: () => api.get('/my-bookings'),
  getUpcomingMatches: (id) => api.get(`/facilities/${id}/upcoming-matches`),
  markBookingPaid: (bookingId) => api.post(`/bookings/${bookingId}/mark-paid`),
  // Bank details & payments
  saveBankDetails: (facilityId, data) => api.put(`/facilities/${facilityId}/bank-details`, data),
  getBankDetails: (facilityId) => api.get(`/facilities/${facilityId}/bank-details`),
  paymentStatus: (facilityId) => api.get(`/facilities/${facilityId}/payment-status`),
  getBalance: (facilityId) => api.get(`/facilities/${facilityId}/balance`),
  getDashboardStats: (facilityId) => api.get(`/facilities/${facilityId}/dashboard-stats`),
  createPayout: (facilityId, data) => api.post(`/facilities/${facilityId}/payouts`, data),
  completePayout: (facilityId, payoutId) => api.post(`/facilities/${facilityId}/payouts/${payoutId}/complete`),
  // Premium subscription
  subscribePremium: (facilityId) => api.post(`/facilities/${facilityId}/subscribe-premium`),
  cancelPremium: (facilityId) => api.post(`/facilities/${facilityId}/cancel-premium`),
};

export const paymentsAPI = {
  getConfig: () => api.get('/stripe/config'),
  createPayment: (bookingId, data = {}) => api.post(`/bookings/${bookingId}/pay`, data),
  confirmPayment: (bookingId, data) => api.post(`/bookings/${bookingId}/confirm-payment`, data),
};

export const matchFieldAPI = {
  assignField: (matchId, data) => api.put(`/matches/${matchId}/assign-field`, data),
  unassignField: (matchId) => api.delete(`/matches/${matchId}/assign-field`),
};

export const uploadAPI = {
  uploadFile: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

export default api;


api.interceptors.response.use(response => {
  const method = response.config?.method?.toLowerCase();
  if (method && method !== 'get' && response.config?.url?.includes('/matches')) {
    window.dispatchEvent(new Event('mvp-reminders-changed'));
  }
  return response;
});
